import { createSlice } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'

import { REGION } from '@/utils/constants'

import { PLACEMENT } from './appSlice'
import { CrownInput } from './stainingStudio'

interface stainingCorrectionRecipe {
  [PLACEMENT.LEFT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
  [PLACEMENT.RIGHT]: {
    [key: string]: {
      input: CrownInput
      output: any
    }
  }
}
export interface pfmCrownState {
  pfmCrown: any
  selectedOpaquerStainingKey: string
  opaquerStainingBrand: string
  selectedOpaquerFrameworkKey: string
  opaquerStainingTargetColor: any
  opaquerStainingOutput: any
  selectedDentinColorKey: string
  dentinColor: {
    [imageId: string]: {
      [key: string]: {
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }
    }
  }
  dentinCeramicBrand: string
  dentinRecipeOutput: any
  internalEffectCeramicBrand: string
  selectedInternalEffect: string
  internalEffects: any
  internalEffectOutput: any
  selectedStainingCorrectionKey: string | null
  stainingCorrectionRecipe: stainingCorrectionRecipe | null
  selectedStainingCorrectionBrand: string | null
  enamelCeramicBrand: string
  enamelTypesByRegion: Record<string, string>
  measuringRegion: string
  enamelRecipe: any
  enamelPosition: any
  opaquerSliderValue: {
    [region: string]: number
  }
  opaquerCeramicBrand: string
  selectedOpaquerKey: string
  opaquer: any
  opaquerRecipe: any
  DentinColorState: any
  availableSpace: string | null
}

const initialOpaquerSliderValue: { [region: string]: number } = Object.keys(
  REGION,
).reduce(
  (acc, key) => {
    acc[key] = 0.5
    return acc
  },
  {} as { [region: string]: number },
)

const initialState: pfmCrownState = {
  opaquerStainingBrand: '',
  pfmCrown: {},
  selectedOpaquerStainingKey: '',
  selectedOpaquerFrameworkKey: '',
  opaquerStainingTargetColor: {},
  opaquerStainingOutput: { [PLACEMENT.LEFT]: {}, [PLACEMENT.RIGHT]: {} },
  availableSpace: null,

  //Dentin
  dentinColor: {},
  selectedDentinColorKey: '',
  dentinCeramicBrand: '',
  dentinRecipeOutput: {},
  DentinColorState: {},

  //Internal Effect
  internalEffectCeramicBrand: '',
  selectedInternalEffect: '',
  internalEffects: {},
  internalEffectOutput: {},

  //Staining correction
  selectedStainingCorrectionKey: null,
  stainingCorrectionRecipe: {
    [PLACEMENT.LEFT]: {},
    [PLACEMENT.RIGHT]: {},
  },
  selectedStainingCorrectionBrand: null,

  //Enamel
  enamelCeramicBrand: '',
  enamelTypesByRegion: {},
  measuringRegion: '',
  enamelRecipe: {},
  enamelPosition: {},

  //Opaquer
  opaquerSliderValue: initialOpaquerSliderValue,
  opaquerCeramicBrand: '',
  selectedOpaquerKey: '',
  opaquer: {},
  opaquerRecipe: {},
}

export const pfmCrownSlice = createSlice({
  name: 'pfmCrown',
  initialState,
  reducers: {
    setOpaquerStainingBrand: (state, action: PayloadAction<string>) => {
      state.opaquerStainingBrand = action.payload
    },
    setSelectedOpaquerStainingKey: (state, action: PayloadAction<string>) => {
      state.selectedOpaquerStainingKey = action.payload
    },
    setOpaquerStainingTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { targetcolor, position, output } = action.payload
      const key = state.selectedOpaquerStainingKey
      if (!key) return

      if (!state.pfmCrown) {
        state.pfmCrown = {}
      }

      const existing = state.pfmCrown[key] || {}

      state.pfmCrown[key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },

    setOpaquerStainingFrameworkKey: (state, action: PayloadAction<string>) => {
      state.selectedOpaquerFrameworkKey = action.payload
    },
    setOpaquerFrameWorkTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor: (number | null | string)[]
        position: 'cervical' | 'middle' | 'incisal'
        imagePosition: { x: number; y: number }
      }>,
    ) => {
      const { targetcolor, position, imagePosition } = action.payload

      if (!state.opaquerStainingTargetColor) {
        state.opaquerStainingTargetColor = {}
      }

      state.opaquerStainingTargetColor[position] = {
        targetcolor,
        imagePosition,
      }
    },
    setOpaquerStainingOutput: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: { input?: any; output?: any }
      }>,
    ) => {
      const { placement, key, data } = action.payload

      const currentRecipe = state.opaquerStainingOutput || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      return {
        ...state,
        opaquerStainingOutput: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [key]: {
              ...(currentRecipe[placement]?.[key] || {}),
              ...data,
            },
          },
        },
      }
    },

    removeOpaquerStainingTarget: (
      state,
      action: PayloadAction<{ key: string }>,
    ) => {
      if (state?.opaquerStainingOutput?.[PLACEMENT.LEFT]) {
        delete state.opaquerStainingOutput[PLACEMENT.LEFT][action.payload.key]
      }
      if (state?.opaquerStainingOutput?.[PLACEMENT.RIGHT]) {
        delete state.opaquerStainingOutput[PLACEMENT.RIGHT][action.payload.key]
      }
    },

    setAvailableSpace(state, action: PayloadAction<string>) {
      state.availableSpace = action.payload
    },

    //Dentin
    setSelectedDentinColorKey: (state, action: PayloadAction<string>) => {
      state.selectedDentinColorKey = action.payload
    },
    setDentinColorTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
        key?: string
      }>,
    ) => {
      const {
        imageId,
        targetcolor,
        position,
        output,
        key: actionKey,
      } = action.payload
      const key = actionKey || state.selectedDentinColorKey
      if (!key || !imageId) return

      if (!state.dentinColor) {
        state.dentinColor = {}
      }

      if (!state.dentinColor[imageId]) {
        state.dentinColor[imageId] = {}
      }

      if (!state.dentinColor[imageId][key]) {
        state.dentinColor[imageId][key] = {}
      }

      const existing = state.dentinColor[imageId][key]

      state.dentinColor[imageId][key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
    setDentinCeramicBrand: (state, action: PayloadAction<string>) => {
      state.dentinCeramicBrand = action.payload
    },
    setdentinRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload
      if (!state.dentinRecipeOutput) {
        state.dentinRecipeOutput = {}
      }

      if (!state.dentinRecipeOutput[imageId]) {
        state.dentinRecipeOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.dentinRecipeOutput[imageId][key]
      } else {
        state.dentinRecipeOutput[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    removeDentinTarget: (
      state,
      action: PayloadAction<{ imageId: string; key: string }>,
    ) => {
      const { imageId, key } = action.payload

      if (state.dentinColor?.[imageId]?.[key]) {
        delete state.dentinColor[imageId][key]

        if (Object.keys(state.dentinColor[imageId]).length === 0) {
          delete state.dentinColor[imageId]
        }
      }

      if (state.DentinColorState?.[imageId]?.[key]) {
        delete state.DentinColorState[imageId][key]
        if (Object.keys(state.DentinColorState[imageId]).length === 0) {
          delete state.DentinColorState[imageId]
        }
      }
    },

    //Internal Effect
    setInternalEffectCeramicBrand: (state, action: PayloadAction<string>) => {
      state.internalEffectCeramicBrand = action.payload
    },
    setSelectedInternalEffect: (state, action: PayloadAction<string>) => {
      state.selectedInternalEffect = action.payload
    },
    setInternalEffectPosition: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, position, output } = action.payload
      const key = state.selectedInternalEffect
      if (!key || !imageId) return

      if (!state.internalEffects) {
        state.internalEffects = {}
      }

      if (!state.internalEffects[imageId]) {
        state.internalEffects[imageId] = {}
      }

      if (!state.internalEffects[imageId][key]) {
        state.internalEffects[imageId][key] = {}
      }

      const existing = state.internalEffects[imageId][key]

      state.internalEffects[imageId][key] = {
        ...existing,
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
    setInternalEffectRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.internalEffectOutput) {
        state.internalEffectOutput = {}
      }

      if (!state.internalEffectOutput[imageId]) {
        state.internalEffectOutput[imageId] = {}
      }

      if (result === undefined) {
        delete state.internalEffectOutput[imageId][key]
      } else {
        state.internalEffectOutput[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },

    //Staining correction
    setSelectedStainingCorrectionKey: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return {
        ...state,
        selectedStainingCorrectionKey: action.payload,
      }
    },
    setSelectedStainngCorrectionBrand: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      return {
        ...state,
        selectedStainingCorrectionBrand: action.payload,
      }
    },
    setStainingCorrectionRecipe: (
      state,
      action: PayloadAction<{
        placement: PLACEMENT
        key: string
        data: {
          input?: CrownInput
          output?: any
        }
      }>,
    ) => {
      const placement = action.payload
        .placement as keyof stainingCorrectionRecipe
      const currentRecipe = state.stainingCorrectionRecipe || {
        [PLACEMENT.LEFT]: {},
        [PLACEMENT.RIGHT]: {},
      }

      const expiresAt = Date.now() + 24 * 60 * 60 * 1000

      return {
        ...state,
        stainingCorrectionRecipe: {
          ...currentRecipe,
          [placement]: {
            ...currentRecipe[placement],
            [action.payload.key]: {
              ...currentRecipe[placement][action.payload.key],
              ...action.payload.data,
              expiresAt,
            },
          },
        },
      }
    },
    clearPfmCrownStainingCorrectionState: () => initialState,
    removeStainingCorrectionKey: (
      state,
      action: PayloadAction<{
        key: string
      }>,
    ) => {
      if (state?.stainingCorrectionRecipe?.[PLACEMENT.LEFT]) {
        delete state.stainingCorrectionRecipe[PLACEMENT.LEFT][
          action.payload.key
        ]
      }
      if (state?.stainingCorrectionRecipe?.[PLACEMENT.RIGHT]) {
        delete state.stainingCorrectionRecipe[PLACEMENT.RIGHT][
          action.payload.key
        ]
      }
    },

    //Enamel
    setEnamelCeramicBrand: (state, action: PayloadAction<string>) => {
      state.enamelCeramicBrand = action.payload
    },
    setEnamelTypeByRegion(
      state,
      action: PayloadAction<{ region: string; value: string }>,
    ) {
      if (action.payload.value === '') {
        delete state.enamelTypesByRegion[action.payload.region]
      } else {
        state.enamelTypesByRegion[action.payload.region] = action.payload.value
      }
    },
    setMeasuringRegion: (state, action) => {
      state.measuringRegion = action.payload
    },
    setEnamelRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.enamelRecipe) {
        state.enamelRecipe = {}
      }

      if (!state.enamelRecipe[imageId]) {
        state.enamelRecipe[imageId] = {}
      }

      if (result === undefined) {
        delete state.enamelRecipe[imageId][key]
      } else {
        state.enamelRecipe[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    setEnamelPosition: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, position, output } = action.payload
      const key = state.measuringRegion
      if (!key || !imageId) return

      if (!state.enamelPosition) {
        state.enamelPosition = {}
      }

      if (!state.enamelPosition[imageId]) {
        state.enamelPosition[imageId] = {}
      }

      if (!state.enamelPosition[imageId][key]) {
        state.enamelPosition[imageId][key] = {}
      }

      const existing = state.enamelPosition[imageId][key]

      state.enamelPosition[imageId][key] = {
        ...existing,
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },

    //Opaquer
    setOpaquerRangeSlider: (
      state,
      action: PayloadAction<{ name: string; value: number }>,
    ) => {
      const { name, value } = action.payload
      state.opaquerSliderValue[name] = value
    },
    setOpaquerCeramicBrand: (state, action: PayloadAction<string>) => {
      state.opaquerCeramicBrand = action.payload
    },
    setSelectedOpaquerKey: (state, action: PayloadAction<string>) => {
      state.selectedOpaquerKey = action.payload
    },
    setOpaquerTarget: (
      state,
      action: PayloadAction<{
        imageId: string
        targetcolor?: (number | null | string)[]
        position?: { x: number; y: number }
        output?: any
      }>,
    ) => {
      const { imageId, targetcolor, position, output } = action.payload
      const key = state.selectedOpaquerKey
      if (!key || !imageId) return

      if (!state.opaquer) {
        state.opaquer = {}
      }

      if (!state.opaquer[imageId]) {
        state.opaquer[imageId] = {}
      }

      if (!state.opaquer[imageId][key]) {
        state.opaquer[imageId][key] = {}
      }

      const existing = state.opaquer[imageId][key]

      state.opaquer[imageId][key] = {
        ...existing,
        ...(targetcolor && { targetcolor }),
        ...(position && { position }),
        ...(output && {
          output: {
            ...existing.output,
            ...output,
          },
        }),
      }
    },
    setOpaquerRecipeOutput: (state, action) => {
      const { imageId, key, result } = action.payload

      if (!state.opaquerRecipe) {
        state.opaquerRecipe = {}
      }

      if (!state.opaquerRecipe[imageId]) {
        state.opaquerRecipe[imageId] = {}
      }

      if (result === undefined) {
        delete state.opaquerRecipe[imageId][key]
      } else {
        state.opaquerRecipe[imageId][key] = {
          ...result,
          generatedAt: Date.now(),
          expiresAt: Date.now() + 24 * 60 * 60 * 1000,
        }
      }
    },
    removeOpaquerTarget: (
      state,
      action: PayloadAction<{ imageId: string; key: string }>,
    ) => {
      const { imageId, key } = action.payload

      if (state.opaquer?.[imageId]?.[key]) {
        delete state.opaquer[imageId][key]

        if (Object.keys(state.opaquer[imageId]).length === 0) {
          delete state.opaquer[imageId]
        }
      }
    },

    setPFMDentinTargetColor: (
      state,
      action: PayloadAction<{
        imageId: string
        key: string
        targetcolor: number[]
      }>,
    ) => {
      const { imageId, key, targetcolor } = action.payload
      if (!imageId || !key) return

      if (!state.DentinColorState[imageId]) {
        state.DentinColorState[imageId] = {}
      }

      state.DentinColorState[imageId][key] = targetcolor
    },

    clearOpaquerStainingState: (state) => {
      state.opaquerStainingOutput = initialState.opaquerStainingOutput
      state.selectedOpaquerKey = initialState.selectedOpaquerKey
      state.selectedOpaquerFrameworkKey =
        initialState.selectedOpaquerFrameworkKey
      state.opaquerStainingTargetColor = initialState.opaquerStainingTargetColor
      state.opaquer = initialState.opaquer
    },
  },
})

export const {
  setOpaquerStainingBrand,
  setSelectedOpaquerStainingKey,
  setOpaquerStainingTarget,
  setOpaquerStainingFrameworkKey,
  setOpaquerFrameWorkTarget,
  setOpaquerStainingOutput,
  removeOpaquerStainingTarget,
  setAvailableSpace,
  clearOpaquerStainingState,

  //Dentin
  setSelectedDentinColorKey,
  setDentinColorTarget,
  setDentinCeramicBrand,
  setdentinRecipeOutput,
  removeDentinTarget,
  setPFMDentinTargetColor,

  //Internal Effect
  setInternalEffectCeramicBrand,
  setSelectedInternalEffect,
  setInternalEffectPosition,
  setInternalEffectRecipeOutput,

  //Staining correction
  setSelectedStainingCorrectionKey,
  setSelectedStainngCorrectionBrand,
  setStainingCorrectionRecipe,
  clearPfmCrownStainingCorrectionState,
  removeStainingCorrectionKey,

  //Enamel
  setEnamelCeramicBrand,
  setEnamelTypeByRegion,
  setMeasuringRegion,
  setEnamelRecipeOutput,
  setEnamelPosition,

  //Opaquer
  setOpaquerRangeSlider,
  setOpaquerCeramicBrand,
  setSelectedOpaquerKey,
  setOpaquerTarget,
  setOpaquerRecipeOutput,
  removeOpaquerTarget,
} = pfmCrownSlice.actions
export default pfmCrownSlice.reducer
